import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, Button, Form, Input, Modal } from 'antd'
import type { z } from 'zod'
import {
  BILL_SEARCH_BATCH_ASYNC_THRESHOLD,
  type billBatchTargetSchema,
  type billSchema
} from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'
import { BillBatchResult } from './bill-batch-result.tsx'
import { BillConfirmationTable } from './bill-confirmation-table.tsx'
import { BillSearchBatchDialog } from './bill-search-batch-dialog.tsx'
import type { BillSearchBatchTaskInfo } from './bill-search-batch-task.tsx'
import { BILL_ACTION_DESCRIPTION, BILL_ACTION_ITEMS } from './const/bill-actions.ts'
import { GroupEditor } from './group-editor.tsx'
import { MoneyEntry } from './money-entry.tsx'
import { batchBillPayload, billBatchTargetError, invalidBatchAmount } from './utils/bill-batch.ts'
import { canEnterUtility, type BillIssueEntry } from './utils/bill-issue.ts'

export type BillOperation = {
  total?: number
  action: (typeof BILL_ACTION_ITEMS)[number]['key']
  target: z.infer<typeof billBatchTargetSchema>
}
type BillActionDialogProps = {
  operation: BillOperation
  onClose: () => void
  onSaved: () => Promise<void>
  onGroup?: (id: number) => void
  onQueued?: (task: BillSearchBatchTaskInfo) => void
}

export function BillActionDialog(props: BillActionDialogProps) {
  const { operation } = props
  if (
    operation.target.mode === 'search' &&
    (operation.total ?? 0) > BILL_SEARCH_BATCH_ASYNC_THRESHOLD &&
    operation.action !== 'money' &&
    operation.action !== 'group' &&
    props.onQueued
  ) {
    return (
      <BillSearchBatchDialog
        action={operation.action}
        search={operation.target.search}
        count={operation.total!}
        onClose={props.onClose}
        onSaved={props.onSaved}
        onQueued={props.onQueued}
      />
    )
  }
  return <BillTargetActionDialog {...props} />
}

function BillTargetActionDialog({
  operation,
  onClose,
  onSaved,
  onGroup
}: {
  operation: BillOperation
  onClose: () => void
  onSaved: () => Promise<void>
  onGroup?: (id: number) => void
}) {
  const [open, setOpen] = useState(true)
  const [savedGroupId, setSavedGroupId] = useState<number>()
  const close = () => setOpen(false)
  const afterClose = () => {
    onClose()
    if (savedGroupId !== undefined) {
      onGroup?.(savedGroupId)
    }
  }
  const trpc = useTRPC()
  const targets = useQuery({
    ...trpc.bill.batchTargets.queryOptions(operation.target),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  const title = BILL_ACTION_ITEMS.find((item) => item.key === operation.action)?.label ?? '账单操作'
  const bills = targets.data
  const targetError = billBatchTargetError({ operation, count: bills?.length, error: targets.error })
  const complete = async () => {
    await onSaved()
    close()
  }
  if (!bills || targets.isFetching || targetError) {
    return (
      <Modal
        open={open}
        title={title}
        onCancel={close}
        afterClose={afterClose}
        footer={<Button onClick={close}>关闭</Button>}
      >
        <Alert
          type={targetError ? 'error' : 'info'}
          showIcon
          title={targetError ?? '正在获取操作范围内的账单…'}
          action={targets.isError ? <Button onClick={() => void targets.refetch()}>重试</Button> : undefined}
        />
      </Modal>
    )
  }
  if (operation.action === 'money') {
    return <MoneyEntry bills={bills} open={open} afterClose={afterClose} onClose={close} onSaved={complete} />
  }
  if (operation.action === 'group') {
    return (
      <GroupEditor
        bills={bills}
        open={open}
        afterClose={afterClose}
        onClose={close}
        onSaved={async (id) => {
          setSavedGroupId(id)
          await complete()
        }}
      />
    )
  }
  return (
    <BillBatchConfirmation
      action={operation.action}
      bills={bills}
      title={title}
      bySearch={operation.target.mode === 'search'}
      open={open}
      afterClose={afterClose}
      onClose={close}
      onSaved={onSaved}
    />
  )
}

function BillBatchConfirmation({
  action,
  bills,
  title,
  bySearch,
  open,
  afterClose,
  onClose,
  onSaved
}: {
  action: 'issue' | 'archive' | 'amount' | 'note' | 'delete'
  bills: z.infer<typeof billSchema>[]
  title: string
  bySearch: boolean
  open: boolean
  afterClose: () => void
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [key] = useState(() => crypto.randomUUID())
  const [entries, setEntries] = useState<Partial<Record<number, BillIssueEntry>>>({})
  const [current, setCurrent] = useState(1)
  const [results, setResults] = useState<{ billId: number; success: boolean; message?: string }[]>()
  const save = useMutation(trpc.bill.batchAction.mutationOptions())
  let modalWidth = 'min(70rem, calc(100vw - 2rem))'
  if (action === 'note') modalWidth = 'min(48rem, calc(100vw - 2rem))'
  if (action === 'issue' && bills.some(canEnterUtility)) modalWidth = 'min(88rem, calc(100vw - 2rem))'
  return (
    <ModalForm<{ reason: string; note: string }>
      open={open}
      title={`${title}（${bills.length} 张）`}
      width={modalWidth}
      layout="vertical"
      modalProps={{
        afterClose,
        onCancel: onClose,
        destroyOnHidden: true,
        mask: { closable: false },
        closable: !save.isPending
      }}
      submitter={
        results
          ? false
          : {
              searchConfig: { submitText: action === 'issue' ? '确认出账' : `确认${title}` },
              submitButtonProps: { loading: save.isPending, danger: action === 'delete' },
              resetButtonProps: { disabled: save.isPending }
            }
      }
      onFinish={async (values) => {
        const invalid = invalidBatchAmount({ action, bills, entries })
        if (invalid) {
          setCurrent(Math.floor(invalid.index / 10) + 1)
          void message.error(invalid.message)
          return false
        }
        try {
          const result = await save.mutateAsync({
            action,
            bills: batchBillPayload({ action, bills, entries }),
            ...(action === 'amount' ? { reason: values.reason } : {}),
            ...(action === 'note' ? { note: values.note } : {}),
            idempotencyKey: key
          })
          setResults(result)
          try {
            await onSaved()
          } catch {
            void message.warning('账单操作已完成，但列表刷新失败，请刷新页面查看最新状态。')
          }
          if (result.every((item) => item.success)) {
            void message.success(`${result.length} 张账单${title}成功`)
            onClose()
          }
          return false
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '操作失败')
          return false
        }
      }}
    >
      <div className="flex flex-col gap-4">
        <BillBatchNotice action={action} count={bills.length} bySearch={bySearch} />
        <BillConfirmationTable
          bills={bills}
          issue={action === 'issue'}
          editAmount={action === 'amount'}
          entries={entries}
          onEntryChange={(id, entry) => setEntries((previous) => ({ ...previous, [id]: entry }))}
          current={current}
          onPageChange={setCurrent}
          disabled={save.isPending || Boolean(results)}
        />
        {action === 'amount' && !results && (
          <div>
            <Form.Item name="reason" label="修改原因" rules={[{ required: true, whitespace: true }]}>
              <Input.TextArea maxLength={1000} showCount />
            </Form.Item>
          </div>
        )}
        {action === 'note' && !results && (
          <Form.Item
            name="note"
            label="新增备注"
            rules={[{ required: true, whitespace: true, message: '请填写备注' }, { max: 5000 }]}
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={5000}
              showCount
              placeholder="填写要追加到所选账单的备注"
            />
          </Form.Item>
        )}
        {results && <BillBatchResult results={results} />}
      </div>
    </ModalForm>
  )
}

function BillBatchNotice({
  action,
  count,
  bySearch
}: {
  action: 'issue' | 'archive' | 'amount' | 'note' | 'delete'
  count: number
  bySearch: boolean
}) {
  return action === 'issue' || action === 'note' ? (
    <p className="text-content-muted text-sm">
      {bySearch && '按搜索操作，包含其他分页。'}
      {BILL_ACTION_DESCRIPTION[action]}
    </p>
  ) : (
    <Alert
      showIcon
      type="warning"
      title={bySearch ? `按搜索操作：全部匹配结果共 ${count} 张，包含其他分页。` : `本次操作 ${count} 张收款账单。`}
      description={BILL_ACTION_DESCRIPTION[action]}
    />
  )
}
